/**
 * Labstep
 *
 * @module grid/Report/components/ColumnHeader/ContextMenu
 * @desc Entity DataGrid Context Menu
 */

import type { FilterMetadataType } from 'labstep-web/components/Filter/Metadata/Form/types';
import { FilterPostReportBoolean } from 'labstep-web/components/FilterPost/Report/Boolean';
import { FilterPostReportDate } from 'labstep-web/components/FilterPost/Report/Date';
import { FilterPostReportEntity } from 'labstep-web/components/FilterPost/Report/Entity';
import { FilterPostReportExperimentAuthor } from 'labstep-web/components/FilterPost/Report/ExperimentAuthor';
import { FilterPostReportMetadata } from 'labstep-web/components/FilterPost/Report/Metadata';
import { FilterPostReportMetadataHasValue } from 'labstep-web/components/FilterPost/Report/MetadataHasValue';
import { FilterPostReportNumeric } from 'labstep-web/components/FilterPost/Report/Numeric';
import { FilterPostReportOptions } from 'labstep-web/components/FilterPost/Report/Options';
import { FilterPostReportResourceLocation } from 'labstep-web/components/FilterPost/Report/ResourceLocation';
import { FilterPostReportText } from 'labstep-web/components/FilterPost/Report/Text';
import { useGridReportServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { useParamsContext } from 'labstep-web/contexts/params/hook';
import { Dropdown } from 'labstep-web/core/Dropdown';
import { DropdownPortal } from 'labstep-web/core/Dropdown/Portal';
import { Icon } from 'labstep-web/core/Icon';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import type { ReportingColDef } from 'labstep-web/grid/Report/coldefs/types';
import { PostFilterFieldTypes } from 'labstep-web/grid/services/grid-filter.service';
import {
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import {
  dateOnlyAtUTC,
  formatDateOnly,
} from 'labstep-web/services/date.service';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadable,
} from 'labstep-web/services/postFilter/constants';
import React from 'react';
import type { GridReportColumnHeaderContextMenuProps } from './types';

export const SUPPORTED_METADATA_TYPES = [
  MetadataType.default,
  MetadataType.numeric,
  MetadataType.date,
  MetadataType.options,
];

export const SUPPORTED_HAS_VALUE_METADATA_TYPES = [
  MetadataType.default,
  MetadataType.numeric,
  MetadataType.date,
  MetadataType.options,
  MetadataType.file,
];

export const GridReportColumnHeaderContextMenu: React.FC<
  GridReportColumnHeaderContextMenuProps
> = ({
  columnApi,
  column,
  firstItems,
  lastItems,
  columnOptions,
  trigger,
}) => {
  const { addPostFilter } = useParamsContext();
  const gridReportService = useGridReportServiceFromContext();
  const dropdownItems: React.ReactNode[] = firstItems
    ? [
        React.cloneElement(firstItems as React.ReactElement, {
          key: 'first-items',
        }),
      ]
    : [];

  dropdownItems.push(
    <Dropdown.Item
      key="sort-asc"
      onClick={(): void => {
        if (gridReportService) {
          gridReportService.sortColumn(
            trigger.props.column.colId,
            'asc',
          );
        }
      }}
    >
      <Icon name="sort amount up" /> Sort ASC by this field
    </Dropdown.Item>,
  );
  dropdownItems.push(
    <Dropdown.Item
      key="sort-desc"
      onClick={(): void => {
        if (gridReportService) {
          gridReportService.sortColumn(
            trigger.props.column.colId,
            'desc',
          );
        }
      }}
    >
      <Icon name="sort amount down" /> Sort DESC by this field
    </Dropdown.Item>,
  );

  const colDef = column.getColDef() as ReportingColDef;

  if (colDef.colId === 'experiment_author') {
    dropdownItems.push(
      <FilterPostReportExperimentAuthor
        key="filter-experiment-author"
        addPostFilter={addPostFilter}
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.metadata &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field &&
    colDef.colId &&
    colDef.headerComponentParams?.postFilterSettings.path
  ) {
    const parts = colDef.colId.split(':');
    if (parts.length >= 4) {
      const type = parts[2];
      const metadataLabel = parts[3];
      if (
        SUPPORTED_HAS_VALUE_METADATA_TYPES.includes(
          type as MetadataType,
        ) &&
        colDef.headerComponentParams?.postFilterSettings?.path
      ) {
        dropdownItems.push(
          <FilterPostReportMetadataHasValue
            key={`filter-metadata-has-value-${colDef.colId}`}
            metadataLabel={metadataLabel}
            path={
              colDef.headerComponentParams?.postFilterSettings.path
            }
            addPostFilter={addPostFilter}
            protocolValueName={
              colDef.headerComponentParams?.postFilterSettings
                ?.protocolValueName
            }
          />,
        );
      }
      dropdownItems.push(
        <FilterPostReportMetadata
          key={`filter-metadata-${colDef.colId}`}
          path={
            colDef.headerComponentParams?.postFilterSettings.path!
          }
          addPostFilter={addPostFilter}
          defaultValues={{
            label: metadataLabel,
            type: {
              value: type as FilterMetadataType,
              label: MetadataTypeValues[type as FilterMetadataType],
            },
          }}
          protocolValueName={
            colDef.headerComponentParams?.postFilterSettings
              ?.protocolValueName
          }
        />,
      );
    }
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.text &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportText
        key={`filter-text-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          comparison: {
            label: 'Exact match',
            value: PostFilterComparison.eq,
          },
          values: '',
        }}
      />,
    );
  }
  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.date &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportDate
        key={`filter-date-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          value: formatDateOnly(dateOnlyAtUTC(new Date())),
        }}
      />,
    );
  }

  if (
    !colDef.colId?.startsWith('metadata:') &&
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.numeric &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportNumeric
        key={`filter-numeric-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          comparison: {
            label: PostFilterComparisonHumanReadable.eq,
            value: PostFilterComparison.eq,
          },
          values: 0,
        }}
        multiplicator={
          colDef.headerComponentParams?.postFilterSettings
            .multiplicator
        }
        protocolValueName={
          colDef.headerComponentParams?.postFilterSettings
            ?.protocolValueName
        }
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.options &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportOptions
        key={`filter-options-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          values: '',
        }}
        entityName={
          colDef.headerComponentParams?.postFilterSettings
            .entityName || ''
        }
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.boolean &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportBoolean
        key={`filter-boolean-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        defaultValues={{
          bool: {
            label: 'True',
            value: true,
          },
        }}
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.custom &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field
  ) {
    dropdownItems.push(
      <FilterPostReportResourceLocation
        key={`filter-custom-${colDef.colId}`}
        addPostFilter={addPostFilter}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        protocolValueName={
          colDef.headerComponentParams?.postFilterSettings
            ?.protocolValueName
        }
      />,
    );
  }

  if (
    colDef.headerComponentParams?.postFilterSettings?.fieldType ===
      PostFilterFieldTypes.entity &&
    colDef.headerName &&
    colDef.headerComponentParams?.postFilterSettings.field &&
    colDef.headerComponentParams?.postFilterSettings.entityName
  ) {
    dropdownItems.push(
      <FilterPostReportEntity
        key={`filter-entity-${colDef.colId}`}
        label={colDef.headerName}
        path={colDef.headerComponentParams?.postFilterSettings.path}
        field={colDef.headerComponentParams?.postFilterSettings.field}
        addPostFilter={addPostFilter}
        entityName={
          colDef.headerComponentParams?.postFilterSettings.entityName
        }
        protocolValueName={
          colDef.headerComponentParams?.postFilterSettings
            ?.protocolValueName
        }
      />,
    );
  }

  const afterDividerItems = [];
  if (GridNewIndexService.isHideable(column)) {
    afterDividerItems.push(
      <Dropdown.Item
        key="hide-column"
        onClick={(): void =>
          columnApi.setColumnVisible(column, false)
        }
      >
        <Icon name="eye slash" /> Hide from View
      </Dropdown.Item>,
    );
  }

  if (lastItems) {
    afterDividerItems.push(
      React.cloneElement(lastItems as React.ReactElement, {
        key: 'last-items',
      }),
    );
  }
  if (dropdownItems.length && afterDividerItems.length) {
    dropdownItems.push(<Dropdown.Divider key="divider-1" />);
  }
  dropdownItems.push(...afterDividerItems);
  if (columnOptions) {
    dropdownItems.push(<Dropdown.Divider key="divider-2" />);
    dropdownItems.push(
      React.cloneElement(columnOptions as React.ReactElement, {
        key: 'column-options',
      }),
    );
  }
  return dropdownItems.length ? (
    <DropdownPortal trigger={trigger}>{dropdownItems}</DropdownPortal>
  ) : (
    trigger
  );
};
